import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errText } from "../lib/api";
import { t } from "../i18n";
import { countries } from "../lib/countries";
import { DateField } from "../components/DateField";
import { Trash2, ShoppingBag, Download, LogOut, ShieldOff, Check, Heart } from "lucide-react";

// Small reusable form field with a proper <label> — matches the Shopify look.
export const Row = ({ label, hint, children, testid }) => (
  <label className="block space-y-1" data-testid={testid ? `${testid}-row` : undefined}>
    <span className="text-xs font-medium text-ink">{label}</span>
    {children}
    {hint ? <span className="block text-[11px] text-muted">{hint}</span> : null}
  </label>
);

// ---- Overview ----
export function OverviewPanel({ account, orders, wishlist, reminders, locale, onGoto }) {
  const nextReminder = (reminders || []).slice().sort((a, b) => (a.date || "").localeCompare(b.date || ""))[0];
  const lastOrder = (orders || [])[0];
  return (
    <div className="space-y-6" data-testid="account-overview">
      <div className="card p-6">
        <p className="smallcaps text-brass">{t(locale, "ovWelcome")}</p>
        <h2 className="font-display text-3xl text-ink mt-1">{account.name || account.email}</h2>
        <p className="text-xs text-muted mt-2">
          {(orders || []).length} {t(locale, "wStatOrders")}
          {" · "}{(wishlist || []).length} {t(locale, "wStatWishlist")}
          {" · "}{(reminders || []).length} {t(locale, "wStatReminders")}
        </p>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="card p-5 flex flex-col gap-3" data-testid="overview-recent-order">
          <p className="smallcaps">{t(locale, "ovLatestOrder")}</p>
          {lastOrder ? (
            <>
              <div>
                <p className="text-sm text-ink">{lastOrder.order_number} — {lastOrder.recipient_name}</p>
                <p className="text-xs text-muted">{lastOrder.city} · {lastOrder.status_label}</p>
              </div>
              <button className="btn-ghost text-xs self-start" onClick={() => onGoto("orders")}>{t(locale, "ovViewOrder")}</button>
            </>
          ) : (
            <>
              <p className="text-sm text-muted">{t(locale, "ovNoOrders")}</p>
              <Link className="btn-primary text-xs self-start" to={`/${locale}`}>{t(locale, "ovStartShopping")}</Link>
            </>
          )}
        </div>
        <div className="card p-5 flex flex-col gap-3" data-testid="overview-next-reminder">
          <p className="smallcaps">{t(locale, "ovNextReminder")}</p>
          {nextReminder ? (
            <>
              <div>
                <p className="text-sm text-ink">{nextReminder.occasion_name} — {nextReminder.recipient_name}</p>
                <p className="text-xs text-muted tabular">{nextReminder.date}</p>
              </div>
              <button className="btn-ghost text-xs self-start" onClick={() => onGoto("reminders")}>{t(locale, "ovManageReminders")}</button>
            </>
          ) : (
            <>
              <p className="text-sm text-muted">{t(locale, "ovNoReminders")}</p>
              <button className="btn-ghost text-xs self-start" onClick={() => onGoto("reminders")}>{t(locale, "ovAddReminder")}</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- Wishlist ----
export function WishlistPanel({ locale }) {
  const [items, setItems] = useState([]);
  const load = () => api.get("/account/wishlist", { params: { locale } }).then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, [locale]); // eslint-disable-line react-hooks/exhaustive-deps
  const remove = async (id) => { await api.delete(`/account/wishlist/${id}`); load(); };
  if (items.length === 0)
    return (
      <div className="card p-8 text-center space-y-4" data-testid="wishlist-empty">
        <Heart size={32} className="mx-auto text-brass" />
        <p className="text-sm text-muted max-w-sm mx-auto">{t(locale, "wlEmpty")}</p>
        <div>
          <Link className="btn-primary text-xs inline-block" to={`/${locale}`}>{t(locale, "wlBrowse")}</Link>
        </div>
      </div>
    );
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="account-wishlist">
      {items.map((w) => (
        <div key={w.id} className="card overflow-hidden" data-testid={`wishlist-${w.product_id}`}>
          <Link to={`/${locale}/product/${w.slug}`}>
            {w.image ? <img src={w.image} alt={w.title} className="w-full aspect-[4/5] object-cover" /> : <div className="aspect-[4/5] bg-plaster" />}
          </Link>
          <div className="p-3 flex items-start justify-between gap-2">
            <div className="min-w-0">
              <Link to={`/${locale}/product/${w.slug}`} className="text-sm text-ink line-clamp-1">{w.title}</Link>
              {w.status !== "published" ? <p className="text-[11px] text-muted">{t(locale, "wlUnavailable")}</p> : null}
            </div>
            <button className="text-muted hover:text-danger p-1" data-testid={`wishlist-remove-${w.product_id}`} onClick={() => remove(w.id)}>
              <Trash2 size={14} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---- Profile ----
export function ProfilePanel({ account, me, locale, onRefresh, onSignedOut }) {
  const [name, setName] = useState(account.name || "");
  const [contact, setContact] = useState({
    phone: me?.phone || "",
    whatsapp: me?.whatsapp || "",
    date_of_birth: me?.date_of_birth || "",
  });
  const [pw, setPw] = useState({ current: "", next: "" });
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setContact({ phone: me?.phone || "", whatsapp: me?.whatsapp || "", date_of_birth: me?.date_of_birth || "" });
  }, [me]);

  const save = async () => {
    setMsg(""); setErr(""); setBusy(true);
    try {
      await api.patch("/account/profile", {
        name, phone: contact.phone, whatsapp: contact.whatsapp, date_of_birth: contact.date_of_birth,
      });
      setMsg(t(locale, "pfSaved")); onRefresh && onRefresh();
    }
    catch (e) { setErr(errText(e)); } finally { setBusy(false); }
  };
  const changePw = async () => {
    setMsg(""); setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      if (data?.token) localStorage.setItem("fl_token", data.token);
      setMsg(t(locale, "pfPwUpdated"));
      setPw({ current: "", next: "" });
    } catch (e) { setErr(errText(e)); } finally { setBusy(false); }
  };
  const exportData = async () => {
    setErr("");
    try {
      const { data } = await api.get("/account/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `fleurinelle-data-${new Date().toISOString().slice(0, 10)}.json`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { setErr(errText(e)); }
  };
  const deleteMe = async () => {
    if (window.prompt(t(locale, "pfDeletePrompt")) !== "DELETE") return;
    setErr(""); setBusy(true);
    try {
      await api.delete("/account/me");
      localStorage.removeItem("fl_token");
      onSignedOut && onSignedOut();
    } catch (e) { setErr(errText(e)); setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="account-profile">
      <div className="card p-5 space-y-3">
        <h2 className="font-display text-xl text-ink">{t(locale, "pfTitle")}</h2>
        <Row label={t(locale, "pfFullName")} testid="profile-name">
          <input className="field" data-testid="profile-name-input" value={name} onChange={(e) => setName(e.target.value)} />
        </Row>
        <Row label={t(locale, "pfEmail")} hint={t(locale, "pfEmailHint")} testid="profile-email">
          <input className="field opacity-60" value={account.email} readOnly data-testid="profile-email-input" />
        </Row>
        <div className="grid sm:grid-cols-2 gap-3">
          <Row label={t(locale, "pfPhone")} hint={t(locale, "pfPhoneHint")} testid="profile-phone">
            <input className="field" data-testid="profile-phone-input" type="tel" inputMode="tel"
                   placeholder="+212 6 12 34 56 78" value={contact.phone}
                   onChange={(e) => setContact({ ...contact, phone: e.target.value })} />
          </Row>
          <Row label={t(locale, "pfWhatsapp")} testid="profile-whatsapp">
            <input className="field" data-testid="profile-whatsapp-input" type="tel" inputMode="tel"
                   placeholder="+212 6 12 34 56 78" value={contact.whatsapp}
                   onChange={(e) => setContact({ ...contact, whatsapp: e.target.value })} />
          </Row>
        </div>
        <Row label={t(locale, "pfDob")} hint={t(locale, "pfDobHint")} testid="profile-dob">
          <DateField testid="profile-dob-input" value={contact.date_of_birth}
                     max={new Date().toISOString().slice(0, 10)}
                     onChange={(v) => setContact({ ...contact, date_of_birth: v })} />
        </Row>
        <button className="btn-primary text-sm self-start" data-testid="profile-save" disabled={busy || !name.trim()} onClick={save}>
          {t(locale, "pfSave")}
        </button>
      </div>

      <div className="card p-5 space-y-3">
        <h2 className="font-display text-xl text-ink">{t(locale, "pfPwTitle")}</h2>
        <Row label={t(locale, "pfCurrentPw")}>
          <input type="password" className="field" data-testid="profile-pw-current"
                 value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} />
        </Row>
        <Row label={t(locale, "pfNewPw")} hint={t(locale, "pfPwHint")}>
          <input type="password" className="field" data-testid="profile-pw-next"
                 value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} />
        </Row>
        <button className="btn-primary text-sm self-start" data-testid="profile-pw-save"
                disabled={busy || pw.current.length < 6 || pw.next.length < 8} onClick={changePw}>
          {t(locale, "pfUpdatePw")}
        </button>
      </div>

      <div className="card p-5 space-y-3">
        <h2 className="font-display text-xl text-ink">{t(locale, "pfPrivacy")}</h2>
        <p className="text-xs text-muted">{t(locale, "pfPrivacyHint")}</p>
        <div className="flex flex-wrap gap-2">
          <button className="btn-ghost text-xs inline-flex items-center gap-1.5" data-testid="profile-export" onClick={exportData}>
            <Download size={13} /> {t(locale, "pfExport")}
          </button>
          <button className="btn-ghost text-xs text-danger inline-flex items-center gap-1.5" data-testid="profile-delete" onClick={deleteMe} disabled={busy}>
            <ShieldOff size={13} /> {t(locale, "pfDelete")}
          </button>
        </div>
      </div>

      {msg ? <p className="text-success text-sm" data-testid="profile-msg">{msg}</p> : null}
      {err ? <p className="text-danger text-sm" data-testid="profile-err">{err}</p> : null}
    </div>
  );
}

// ---- Preferences ----
export function PreferencesPanel({ me, locale, currencies, onSaved }) {
  const [prefs, setPrefs] = useState({
    marketing_email: me?.preferences?.marketing_email ?? true,
    marketing_whatsapp: me?.preferences?.marketing_whatsapp ?? false,
    occasion_reminders: me?.preferences?.occasion_reminders ?? true,
    locale: me?.locale || locale,
    currency: me?.currency || "EUR",
  });
  const [msg, setMsg] = useState(""); const [err, setErr] = useState("");
  const currencyList = (currencies && currencies.length) ? currencies : [{ code: "EUR" }, { code: "USD" }, { code: "GBP" }, { code: "MAD" }];
  const save = async () => {
    setMsg(""); setErr("");
    try { await api.put("/account/preferences", prefs); setMsg(t(locale, "pfSaved")); onSaved && onSaved(); }
    catch (e) { setErr(errText(e)); }
  };
  const Toggle = ({ k, label, hint, testid }) => (
    <label className="flex items-start justify-between gap-3 py-3 border-b border-brass-light last:border-0 cursor-pointer">
      <div className="min-w-0">
        <p className="text-sm text-ink">{label}</p>
        {hint ? <p className="text-xs text-muted mt-0.5">{hint}</p> : null}
      </div>
      <input type="checkbox" data-testid={testid} className="mt-1 shrink-0"
             checked={!!prefs[k]} onChange={(e) => setPrefs({ ...prefs, [k]: e.target.checked })} />
    </label>
  );
  return (
    <div className="space-y-4" data-testid="account-preferences">
      <div className="card p-5">
        <h2 className="font-display text-xl text-ink">{t(locale, "prMarketing")}</h2>
        <p className="text-xs text-muted mb-2">{t(locale, "prUnsub")}</p>
        <Toggle k="marketing_email" label={t(locale, "prEmailOpt")} testid="pref-marketing-email" />
        <Toggle k="marketing_whatsapp" label={t(locale, "prWhatsappOpt")} testid="pref-marketing-whatsapp" />
        <Toggle k="occasion_reminders" label={t(locale, "prReminderOpt")} testid="pref-occasion-reminders" hint={t(locale, "prReminderHint")} />
      </div>
      <div className="card p-5 space-y-3">
        <h2 className="font-display text-xl text-ink">{t(locale, "prDisplay")}</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          <Row label={t(locale, "prLanguage")}>
            <select className="field" data-testid="pref-locale" value={prefs.locale}
                    onChange={(e) => setPrefs({ ...prefs, locale: e.target.value })}>
              <option value="en">English</option><option value="fr">Français</option>
              <option value="es">Español</option><option value="it">Italiano</option>
              <option value="nl">Nederlands</option><option value="de">Deutsch</option><option value="ar">العربية</option>
            </select>
          </Row>
          <Row label={t(locale, "prCurrency")}>
            <select className="field" data-testid="pref-currency" value={prefs.currency}
                    onChange={(e) => setPrefs({ ...prefs, currency: e.target.value })}>
              {currencyList.map((c) => (
                <option key={c.code} value={c.code}>{c.symbol && c.symbol !== c.code ? `${c.code} (${c.symbol})` : c.code}</option>
              ))}
            </select>
          </Row>
        </div>
      </div>
      <button className="btn-primary text-sm inline-flex items-center gap-1" data-testid="pref-save" onClick={save}>
        <Check size={14} /> {t(locale, "pfSave")}
      </button>
      {msg ? <p className="text-success text-sm" data-testid="pref-msg">{msg}</p> : null}
      {err ? <p className="text-danger text-sm" data-testid="pref-err">{err}</p> : null}
    </div>
  );
}

// ---- Billing address ----
export function BillingAddressPanel({ me, locale, onSaved }) {
  const [b, setB] = useState({
    full_name: me?.billing_address?.full_name || me?.name || "",
    line1: me?.billing_address?.line1 || "",
    line2: me?.billing_address?.line2 || "",
    postal_code: me?.billing_address?.postal_code || "",
    city: me?.billing_address?.city || "",
    country: me?.billing_address?.country || "",
  });
  const [msg, setMsg] = useState(""); const [err, setErr] = useState("");
  const countryList = countries(locale);
  const save = async () => {
    setMsg(""); setErr("");
    try { await api.put("/account/billing-address", b); setMsg(t(locale, "pfSaved")); onSaved && onSaved(); }
    catch (e) { setErr(errText(e)); }
  };
  return (
    <div className="card p-5 space-y-4" data-testid="account-billing-address">
      <div>
        <h2 className="font-display text-xl text-ink">{t(locale, "blTitle")}</h2>
      </div>
      <Row label={t(locale, "blAddress")}>
        <input className="field" data-testid="billing-line1-input" placeholder="12 rue…" value={b.line1} onChange={(e) => setB({ ...b, line1: e.target.value })} />
      </Row>
      <Row label={t(locale, "blApt")}>
        <input className="field" data-testid="billing-line2-input" value={b.line2} onChange={(e) => setB({ ...b, line2: e.target.value })} />
      </Row>
      <div className="grid sm:grid-cols-3 gap-3">
        <Row label={t(locale, "blPostal")}>
          <input className="field" data-testid="billing-postal-input" value={b.postal_code} onChange={(e) => setB({ ...b, postal_code: e.target.value })} />
        </Row>
        <Row label={t(locale, "blCity")}>
          <input className="field" data-testid="billing-city-input" value={b.city} onChange={(e) => setB({ ...b, city: e.target.value })} />
        </Row>
        <Row label={t(locale, "blCountry")}>
          <select className="field" data-testid="billing-country-input" value={b.country} onChange={(e) => setB({ ...b, country: e.target.value })}>
            <option value="">{t(locale, "blChoose")}</option>
            {countryList.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select>
        </Row>
      </div>
      <button className="btn-primary text-sm self-start" data-testid="billing-save"
              disabled={!b.line1 || !b.city || !b.country} onClick={save}>
        {t(locale, "pfSave")}
      </button>
      {msg ? <p className="text-success text-sm" data-testid="billing-msg">{msg}</p> : null}
      {err ? <p className="text-danger text-sm" data-testid="billing-err">{err}</p> : null}
    </div>
  );
}

// ---- Payment methods placeholder ----
export function PaymentMethodsPanel({ locale }) {
  return (
    <div className="card p-8 space-y-3 text-center" data-testid="account-payment-methods">
      <ShoppingBag size={30} className="mx-auto text-brass" />
      <h2 className="font-display text-xl text-ink">{t(locale, "scTitle")}</h2>
      <p className="text-sm text-muted max-w-md mx-auto">{t(locale, "scBody")}</p>
      <span className="inline-block text-[11px] px-2 py-1 border border-brass-light text-muted rounded-sm">{t(locale, "scComingSoon")}</span>
    </div>
  );
}

export const SidebarSignOut = ({ onSignOut, locale }) => (
  <button onClick={onSignOut} className="w-full text-left px-3 py-2 text-xs text-muted hover:text-danger flex items-center gap-2 border-t border-brass-light mt-2 pt-3" data-testid="account-signout">
    <LogOut size={13} /> {t(locale, "accSignOut")}
  </button>
);
